import axios from "../config/axios";
import {
  AuthorizationCompletionResponse,
  LoginCredentials,
  AuthResponse,
  ProfileResponse,
  ValidationResponse,
  User,
} from "../types";

class AuthService {
  async loginUser(
    credentials: LoginCredentials,
    redirectUrl?: string
  ): Promise<AuthResponse> {
    const response = await axios.post<AuthResponse>(
      "/api/auth/login/user",
      credentials,
      {
        params: redirectUrl ? { redirect: redirectUrl } : undefined,
      }
    );
    return response.data;
  }

  async loginAdmin(
    credentials: LoginCredentials,
    redirectUrl?: string
  ): Promise<AuthResponse> {
    const response = await axios.post<AuthResponse>(
      "/api/auth/login/admin",
      credentials,
      {
        params: redirectUrl ? { redirect: redirectUrl } : undefined,
      }
    );
    return response.data;
  }

  // Whether this browser is signed in to SATashkent, through /api/auth/session:
  // the same check every app uses, so the portal and the apps never disagree.
  // It renews from the refresh cookie when the 30-minute access cookie has
  // lapsed, and answers "signed out" once the session was ended anywhere.
  private async session(type: "user" | "admin"): Promise<ValidationResponse> {
    const response = await axios.get<{ active: boolean; user?: User }>("/api/auth/session", {
      params: { type },
    });
    return { valid: !!response.data.active, user: response.data.user } as ValidationResponse;
  }

  async validateUser(): Promise<ValidationResponse> {
    return this.session("user");
  }

  async validate(): Promise<ValidationResponse> {
    const response = await axios.get<ValidationResponse>("/api/auth/validate");
    return response.data;
  }

  async validateAdmin(): Promise<ValidationResponse> {
    return this.session("admin");
  }

  async completeAuthorization(
    tokenId: string,
    type?: "user" | "admin"
  ): Promise<AuthorizationCompletionResponse> {
    const response = await axios.get<AuthorizationCompletionResponse>(
      "/api/auth/authorize/complete",
      {
        params: { token_id: tokenId, ...(type ? { type } : {}) },
      }
    );
    return response.data;
  }

  async logout(): Promise<void> {
    await axios.post("/api/auth/logout");
  }

  async getProfile(type?: "user" | "admin"): Promise<ProfileResponse> {
    const response = await axios.get<ProfileResponse>("/api/auth/profile", {
      params: type ? { type } : undefined,
    });
    return response.data;
  }

  async updateProfile(
    payload: Record<string, unknown>,
    type?: "user" | "admin"
  ): Promise<ProfileResponse> {
    const response = await axios.patch<ProfileResponse>("/api/auth/profile", payload, {
      params: type ? { type } : undefined,
    });
    return response.data;
  }

  async updateProfileImage(file: File, type?: "user" | "admin"): Promise<{ image: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.put<{ image: string }>("/api/auth/profile/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: type ? { type } : undefined,
    });
    return response.data;
  }

  saveUser(user: User): void {
    localStorage.setItem("sso_user", JSON.stringify(user));
  }

  getUser(): User | null {
    const userStr = localStorage.getItem("sso_user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  clearUser(): void {
    localStorage.removeItem("sso_user");
  }

  clearAllData(): void {
    localStorage.clear();
    sessionStorage.clear();

    // Clear cookies
    document.cookie.split(";").forEach((c) => {
      const cookieName = c.replace(/^ +/, "").split("=")[0];
      const expires = `expires=${new Date(0).toUTCString()};path=/`;

      document.cookie = `${cookieName}=;${expires}`;
      document.cookie = `${cookieName}=;${expires};domain=localhost`;
    });
  }
}

const authService = new AuthService();
export default authService;
